import { customElement, property, state } from "lit/decorators.js";
import { css, html, nothing, TemplateResult } from "lit";
import { LitElementBase } from "../LitElementBase";

interface ContextNavItem {
  id: string;
  name: string;
}

@customElement("ehm-context-nav")
export class ContextNav extends LitElementBase {
  static styles = css`
    :host {
      display: block;
      position: sticky;
      top: 120px;
    }
    :host(.nav-no-fit) {
      position: static;
    }
    .context-nav {
      & a {
        color: var(--text-secondary);
        text-decoration: none;
        font-size: var(--font-text-md);
        display: block;
        padding: 12px 0;
        padding-left: 1rem;
        transition: color 0.2s ease;
        font-weight: 400;
      }
      & a:hover {
        text-decoration: none;
        color: var(--text-primary);
      }
      & a.active {
        color: var(--text-primary);
      }
      & ul {
        &:before {
          content: "";
          position: absolute;
          display: block;
          width: 2px;
          top: 0;
          bottom: 0;
          left: 0;
          background-color: var(--border-grey);
        }
      }
    }
    .context-nav__list {
      position: relative;
    }
    .context-nav__indicator {
      display: block;
      position: absolute;
      left: 0;
      top: 0;
      width: 2px;
      z-index: 1;
      background-color: var(--border-brand);
      transition: all 0.2s ease-in-out;
    }
    .context-nav__title {
      font: var(--font-text-xl);
      font-weight: 600;
      margin-bottom: 1rem;
      display: block;
    }
    .context-nav-mobile {
      margin-bottom: 1rem;
      margin-top: -2rem;
      & li:not(:last-child) {
        margin-bottom: 1rem;
      }
      & li a {
        color: var(--text-primary);
        text-decoration: none;
        font-weight: var(--text-regular);
      }
    }

    @media (max-width: 768px) {
      :host {
        position: static;
        margin-left: 0;
        max-width: none;
      }
    }
  `;

  #EXCLUDED_CLASSES = [
    "exclude-from-context-nav",
    "card__content__heading",
    "article-card__heading",
  ];
  #intersectionObserver: IntersectionObserver | undefined;
  #resizeObserver: ResizeObserver | undefined;
  #intersectingHeadings = new Map<string, boolean>();

  @state()
  private items: ContextNavItem[] = [];

  @state()
  private isSmallScreen: boolean;

  @state()
  private activeId: string | undefined;

  @state()
  private navFitsOnScreen: boolean = true;

  @property()
  label: string = "Hitta på sidan";

  @property({ attribute: "for" })
  wrappingElementId?: string | undefined = undefined;

  constructor() {
    super();
    const mql = window.matchMedia("(max-width: 768px)");
    this.isSmallScreen = mql.matches;
    mql.addEventListener("change", (e: MediaQueryListEvent) => {
      this.isSmallScreen = e.matches;
    });
  }

  firstUpdated() {
    this.initItems();
    this.setupIntersectionObserver();
    this.setupResizeObserver();
  }

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(changedProperties);
    if (changedProperties.has("activeId")) {
      this.updateIndicatorPosition();
    }
    if (changedProperties.has("navFitsOnScreen")) {
      this.updatePositionClass();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }
  }

  private initItems() {
    const mainElement = this.wrappingElementId
      ? document.getElementById(this.wrappingElementId)
      : document.querySelector("main");

    const headings = mainElement?.querySelectorAll("h2");
    if (headings) {
      this.items = Array.from(headings)
        .filter(
          (heading) =>
            !this.#EXCLUDED_CLASSES.some((className) =>
              heading.classList.contains(className),
            ),
        )
        .map((heading) => {
          const id = heading.id || crypto.randomUUID();
          heading.id = id;
          return {
            id,
            name: heading.textContent || "",
          };
        });
    }
  }

  private setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: "-120px 0px -50% 0px",
      threshold: 0,
    };

    this.#intersectionObserver = new IntersectionObserver((entries) => {
      // Update persistent map — tracks ALL headings' intersection state across batches.
      // The callback only receives entries that changed, not all currently-intersecting
      // headings, so we must accumulate state here to avoid activating the wrong heading.
      entries.forEach((entry) => {
        this.#intersectingHeadings.set(entry.target.id, entry.isIntersecting);
      });

      // Filter this.items (already in document order) using the full accumulated map
      const visibleItems = this.items.filter(
        (item) => this.#intersectingHeadings.get(item.id) === true,
      );

      if (visibleItems.length > 0) {
        // First in document order = topmost visible heading
        this.setActiveId(visibleItems[0].id);
      } else {
        // Fallback: find the last heading that has scrolled past the 120px trigger
        const aboveThreshold = this.items
          .map((item) => ({
            item,
            rect: document.getElementById(item.id)?.getBoundingClientRect(),
          }))
          .filter(({ rect }) => rect !== undefined && rect.top <= 120);
        const closestAbove = aboveThreshold[aboveThreshold.length - 1]; // last in document order = closest to trigger point

        if (closestAbove) {
          this.setActiveId(closestAbove.item.id);
        }
        // If nothing qualifies, user is above all headings — leave activeId unchanged
      }
    }, options);

    // Observe all headings
    this.items.forEach((item) => {
      const element = document.getElementById(item.id);
      if (element && this.#intersectionObserver) {
        this.#intersectionObserver.observe(element);
      }
    });
  }

  private setActiveId(id: string) {
    if (this.activeId !== id) {
      this.activeId = id;
    }
  }

  private setupResizeObserver() {
    this.#resizeObserver = new ResizeObserver(() => {
      this.checkNavFit();
    });

    if (this.shadowRoot) {
      const nav = this.shadowRoot.querySelector(".context-nav");
      if (nav) {
        this.#resizeObserver.observe(nav);
      }
    }

    // Also check on window resize
    window.addEventListener("resize", () => this.checkNavFit());
  }

  private checkNavFit() {
    if (this.isSmallScreen) {
      this.navFitsOnScreen = true;
      return;
    }

    const nav = this.shadowRoot?.querySelector(".context-nav") as HTMLElement;
    if (!nav) return;

    const navHeight = nav.offsetHeight;
    const topOffset = 120; // Same as sticky top position
    const availableHeight = window.innerHeight - topOffset;

    this.navFitsOnScreen = navHeight <= availableHeight;
  }

  private updatePositionClass() {
    if (!this.navFitsOnScreen) {
      this.classList.add("nav-no-fit");
    } else {
      this.classList.remove("nav-no-fit");
    }
  }

  private updateIndicatorPosition() {
    if (!this.activeId) return;

    const navIndicator = this.shadowRoot?.querySelector(
      ".context-nav__indicator",
    ) as HTMLElement;
    const activeLink = this.shadowRoot?.querySelector(`a[href="#${this.activeId}"]`);

    if (activeLink && navIndicator) {
      const linkRect = activeLink.getBoundingClientRect();
      const navRect = this.shadowRoot
        ?.querySelector(".context-nav__list")
        ?.getBoundingClientRect();

      if (navRect) {
        const relativeTop = linkRect.top - navRect.top;
        navIndicator.style.transform = `translateY(${relativeTop}px)`;
        navIndicator.style.height = `${linkRect.height}px`;
      }
    }
  }

  private handleNavClick(event: Event, targetId: string) {
    event.preventDefault();
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      const headerOffset = 120;
      const elementPosition = targetElement.offsetTop;
      const offsetPosition = elementPosition - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  }

  render(): TemplateResult | null {
    if (this.items.length === 0) return null;

    const items = this.items.map((item) => {
      const isActive = this.activeId === item.id;
      return html`
        <li>
          <a
            href="#${item.id}"
            class="${isActive ? "active" : ""}"
            aria-current="${isActive ? "true" : "false"}"
            @click="${(e: Event) => this.handleNavClick(e, item.id)}"
          >
            ${item.name}
          </a>
        </li>
      `;
    });

    if (this.isSmallScreen) {
      return html`
        <nav class="context-nav-mobile" aria-labelledby="title">
          <details class="accordion">
            <summary class="accordion__title accordion__title--caret" id="title">
              ${this.label}
              <ehm-icon name="caret"></ehm-icon>
            </summary>
            <div class="accordion__content">
              <ul>
                ${items}
              </ul>
            </div>
          </details>
        </nav>
      `;
    }

    return html`
      <nav class="context-nav" aria-labelledby="title">
        <span class="context-nav__title" id="title">${this.label}</span>
        <div class="context-nav__list">
          <span class="context-nav__indicator"></span>
          <ul>
            ${items}
          </ul>
        </div>
      </nav>
    `;
  }
}
